
import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Chip, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, InputAdornment, Grow, Stack, Divider, Card, Grid
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import Loading from '../components/Loading';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpIcon from '@mui/icons-material/Help';
import Tooltip from '@mui/material/Tooltip';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function LocationMarker({ position, setPosition }) {
  const map = useMapEvents({
    click(e) {
      if (setPosition) {
          setPosition(e.latlng);
          map.flyTo(e.latlng, map.getZoom());
      }
    },
  });

  return position ? <Marker position={position} /> : null;
}

function PunchingDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');
  
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  
  // Edit State
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({ id: null, check_in_location: '', check_out_location: '' });
  const [updating, setUpdating] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [userLocation, setUserLocation] = useState(null);
  const [userPunchingId, setUserPunchingId] = useState('');
  const [editLocOpen, setEditLocOpen] = useState(false);
  const [newUserLocation, setNewUserLocation] = useState({ address: '', lat: 0, lng: 0 });

  // Distance Calculation Helper (Haversine Formula) in Meters
  const getDistanceFromLatLonInMeters = (lat1, lon1, lat2, lon2) => {
    var R = 6371; // Radius of the earth in km
    var dLat = deg2rad(lat2-lat1);  // deg2rad below
    var dLon = deg2rad(lon2-lon1); 
    var a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2)
      ; 
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
    var d = R * c; // Distance in km
    return d * 1000; // Meters
  }

  const deg2rad = (deg) => {
    return deg * (Math.PI/180)
  }

  const isLocationMatch = (logLocString) => {
      // 1. Check if user has assigned location
      if (!userLocation || !userLocation.latitude || !userLocation.longitude) return 'unknown'; // Grey
      
      // 2. Parse log location
      if (!logLocString || typeof logLocString !== 'string') return 'unknown';
      const parts = logLocString.split(',');
      if (parts.length !== 2) return 'unknown'; // Likely an address manually typed
      
      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);
      
      if (isNaN(lat) || isNaN(lng)) return 'unknown';
      
      // 3. Calculate Distance
      const dist = getDistanceFromLatLonInMeters(userLocation.latitude, userLocation.longitude, lat, lng);
      
      // 4. Threshold (e.g. 500 meters)
      return dist <= 500 ? 'match' : 'mismatch';
  }

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const [res, locRes] = await Promise.all([
             axios.get(`${API_URL}/punching/attendance/${id}`, { headers }),
             axios.get(`${API_URL}/punching/locations`, { headers }) // We need to filter this manually or need a new endpoint
        ]);
        
        setAttendance(res.data);
        if (res.data.length > 0) {
            setUserName(res.data[0].user_name);
        }

        // Find assigned location for this user
        const assigned = locRes.data.find(l => l.user_id === parseInt(id));
        if (assigned) {
            setUserLocation(assigned);
            setUserPunchingId(assigned.userpunchingid || '');
            setNewUserLocation({ address: assigned.address, lat: assigned.latitude, lng: assigned.longitude, userpunchingid: assigned.userpunchingid || '' });
        }

      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [id]);

  const handleUpdateLocation = async () => {
      if (!userLocation) return;
      try {
          await axios.put(`${API_URL}/punching/locations/${userLocation.id}`, {
              address: newUserLocation.address,
              latitude: newUserLocation.lat,
              longitude: newUserLocation.lng,
              userpunchingid: newUserLocation.userpunchingid
          }, { headers });
          
          setUserLocation({ ...userLocation, address: newUserLocation.address, latitude: newUserLocation.lat, longitude: newUserLocation.lng, userpunchingid: newUserLocation.userpunchingid });
          setUserPunchingId(newUserLocation.userpunchingid || '');
          setEditLocOpen(false);
          alert('Assigned location updated');
      } catch (e) {
          alert('Error updating location');
      }
  }

  const handleEditClick = (row) => {
      setEditData({
          id: row.id,
          check_in_location: row.check_in_location || '',
          check_out_location: row.check_out_location || '-'
      });
      setEditOpen(true);
  };

  const handleSaveEdit = async () => {
      setUpdating(true);
      try {
          // Prepare payload - only send if not dash
          const payload = {
              check_in_location: editData.check_in_location
          };
          if (editData.check_out_location !== '-') {
              payload.check_out_location = editData.check_out_location;
          }

          await axios.put(`${API_URL}/punching/attendance/${editData.id}`, payload, { headers });
          
          // Update local state
          setAttendance(prev => prev.map(p => p.id === editData.id ? { 
              ...p, 
              check_in_location: editData.check_in_location,
              check_out_location: editData.check_out_location === '-' ? p.check_out_location : editData.check_out_location 
          } : p));
          
          setEditOpen(false);
      } catch (err) {
          alert('Failed to update location');
      } finally {
          setUpdating(false);
      }
  };

  const handleExport = () => {
    const hdrs = ["Date", "In Time", "In Location", "Out Time", "Out Location", "Status"];
    const csvContent = [
        hdrs.join(','),
        ...filteredAttendance.map(row => [
            row.date,
            row.check_in,
            `"${row.check_in_location}"`,
            row.check_out,
            `"${row.check_out_location}"`,
            row.check_out !== '-' ? 'Completed' : 'Active'
        ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Attendance_${userName || 'User'}_${filterFrom || 'start'}_to_${filterTo || 'end'}.csv`;
    a.click();
  };

  if (loading) return <Loading message="Loading Attendance Records..." />;

  const filteredAttendance = attendance.filter(row => {
    const fromMatch = filterFrom ? row.date >= filterFrom : true;
    const toMatch = filterTo ? row.date <= filterTo : true;
    const statusMatch = filterStatus === 'All' ? true :
        filterStatus === 'Active' ? row.check_out === '-' :
        row.check_out !== '-';
    return fromMatch && toMatch && statusMatch;
  });

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/punching')} sx={{ color: 'text.secondary', fontWeight: 'bold', '&:hover': { bgcolor: '#f1f5f9' } }}>
            Back to Assignments
        </Button>
      </Box>

      {/* Modern Gradient Header */}
      <Grow in={true} timeout={500}>
        <Box sx={{ 
            mb: 4, 
            p: 4, 
            borderRadius: 4, 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
            color: 'white',
            boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 3
        }}>
            <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Typography variant="h4" fontWeight="800" sx={{ mb: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
                    <PersonIcon sx={{ fontSize: 36, color: '#90caf9' }} />
                    {userName || 'User Details'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        Attendance Logs & Location Tracking
                    </Typography>
                    {userPunchingId && (
                        <Chip
                            label={`Punching ID: ${userPunchingId}`}
                            size="small"
                            sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 'bold' }}
                        />
                    )}
                    {userLocation && (
                        <Chip 
                            label={userLocation.address || 'Location Assigned'} 
                            size="small" 
                            icon={<EditIcon sx={{ fontSize: 14, color: 'white !important' }} />} 
                            onClick={() => setEditLocOpen(true)}
                            sx={{ 
                                cursor: 'pointer', 
                                bgcolor: 'rgba(255,255,255,0.15)', 
                                color: 'white',
                                fontWeight: 'bold',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                                '& .MuiChip-icon': { color: 'white' }
                            }}
                        />
                    )}
                </Box>
            </Box>
            
            {/* Decorative background circles */}
            <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
            <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        </Box>
      </Grow>

      {/* Filters and Controls */}
      <Card elevation={0} sx={{ mb: 4, p: 2, borderRadius: 4, border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'flex-end', alignItems: { xs: 'stretch', md: 'center' }, gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center' }}>
                <TextField
                    label="From"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={filterFrom}
                    onChange={(e) => setFilterFrom(e.target.value)}
                    sx={{ 
                        minWidth: 180,
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#f8fafc',
                            borderRadius: 2,
                            '& fieldset': { borderColor: '#e2e8f0' }
                        }
                    }}
                />
                <TextField
                    label="To"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={filterTo}
                    onChange={(e) => setFilterTo(e.target.value)}
                    sx={{ 
                        minWidth: 180,
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#f8fafc',
                            borderRadius: 2,
                            '& fieldset': { borderColor: '#e2e8f0' }
                        }
                    }}
                />
                <Button 
                    variant="outlined" 
                    startIcon={<DownloadIcon />} 
                    onClick={handleExport}
                    sx={{ borderRadius: 2, fontWeight: 'bold', px: 3, borderColor: '#e2e8f0', color: '#475569', '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' }, height: '40px' }}
                >
                    Export CSV
                </Button>
            </Box>
        </Box>
      </Card>

      {filteredAttendance.length === 0 ? (
          <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: 4, bgcolor: '#f8fafc' }}>
              <Typography color="text.secondary">No attendance records found for this filter.</Typography>
          </Paper>
      ) : (
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 4, border: '1px solid #f1f5f9', overflowX: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
            <Table sx={{ minWidth: 650 }}>
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                    <TableRow>
                        <TableCell sx={{ fontWeight: '800', color: '#475569', py: 2 }}>DATE</TableCell>
                        <TableCell sx={{ fontWeight: '800', color: '#475569', py: 2 }}>IN TIME</TableCell>
                        <TableCell sx={{ fontWeight: '800', color: '#475569', py: 2 }}>IN LOCATION</TableCell>
                        <TableCell sx={{ fontWeight: '800', color: '#475569', py: 2 }}>OUT TIME</TableCell>
                        <TableCell sx={{ fontWeight: '800', color: '#475569', py: 2 }}>OUT LOCATION</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filteredAttendance.map((row) => (
                        <TableRow 
                            key={row.id} 
                            hover
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                            <TableCell sx={{ fontWeight: '600', color: '#1e293b' }}>{row.date}</TableCell>
                            <TableCell sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                {row.check_in}
                            </TableCell>
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: '#f1f5f9', px: 1, py: 0.5, borderRadius: 1, color: '#475569' }}>
                                        {row.check_in_location}
                                    </Typography>
                                    {(() => {
                                        const status = isLocationMatch(row.check_in_location);
                                        if (status === 'match') return <Tooltip title="Matches Assigned Location"><CheckCircleIcon color="success" fontSize="small" /></Tooltip>;
                                        if (status === 'mismatch') return <Tooltip title="Different from Assigned Location"><CancelIcon color="error" fontSize="small" /></Tooltip>;
                                        return <Tooltip title="Cannot verify location (Not coordinates or no assigned location)"><HelpIcon color="disabled" fontSize="small" /></Tooltip>;
                                    })()}
                                </Box>
                            </TableCell>
                            <TableCell sx={{ color: 'error.main', fontWeight: 'bold' }}>
                                {row.check_out}
                            </TableCell>
                            <TableCell>
                                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: '#f1f5f9', px: 1, py: 0.5, borderRadius: 1, color: '#475569' }}>
                                        {row.check_out_location}
                                    </Typography>
                                    {(() => {
                                        if (!row.check_out_location || row.check_out_location === '-') return null;
                                        const status = isLocationMatch(row.check_out_location);
                                        if (status === 'match') return <Tooltip title="Matches Assigned Location"><CheckCircleIcon color="success" fontSize="small" /></Tooltip>;
                                        if (status === 'mismatch') return <Tooltip title="Different from Assigned Location"><CancelIcon color="error" fontSize="small" /></Tooltip>;
                                        return <Tooltip title="Cannot verify location"><HelpIcon color="disabled" fontSize="small" /></Tooltip>;
                                    })()}
                                </Box>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
          </TableContainer>
        )}

      {/* Edit Dialog */}
      <Dialog 
          open={editOpen} 
          onClose={() => setEditOpen(false)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{ sx: { borderRadius: 4, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' } }}
      >
          <DialogTitle sx={{ 
              fontWeight: '800', 
              background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
              color: 'white',
              display: 'flex', 
              alignItems: 'center', 
              gap: 1.5,
              p: 3
          }}>
              <EditIcon /> Edit Computed Location
          </DialogTitle>
          <DialogContent dividers sx={{ p: 4, bgcolor: '#f8fafc' }}>
              <Stack spacing={3} sx={{ mt: 1 }}>
                  <Box>
                      <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>CHECK IN</Typography>
                      <TextField 
                          label="Location Coordinates" 
                          fullWidth 
                          value={editData.check_in_location}
                          onChange={(e) => setEditData({...editData, check_in_location: e.target.value})}
                          sx={{ bgcolor: 'white' }}
                      />
                  </Box>
                  {editData.check_out_location !== '-' && (
                      <Box>
                          <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>CHECK OUT</Typography>
                           <TextField 
                              label="Location Coordinates" 
                              fullWidth 
                              value={editData.check_out_location}
                              onChange={(e) => setEditData({...editData, check_out_location: e.target.value})}
                              sx={{ bgcolor: 'white' }}
                          />
                      </Box>
                  )}
              </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', gap: 1 }}>
              <Button onClick={() => setEditOpen(false)} sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Cancel</Button>
              <Button variant="contained" onClick={handleSaveEdit} disabled={updating} sx={{ borderRadius: 2, px: 3, fontWeight: 'bold' }}>
                  {updating ? 'Saving...' : 'Save Changes'}
              </Button>
          </DialogActions>
      </Dialog>

      {/* Edit Assigned Location Dialog */}
      <Dialog 
          open={editLocOpen} 
          onClose={() => setEditLocOpen(false)} 
          maxWidth="md" 
          fullWidth
          PaperProps={{ sx: { borderRadius: 4, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' } }}
      >
          <DialogTitle sx={{ 
              fontWeight: '800', 
              background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
              color: 'white',
              display: 'flex', 
              alignItems: 'center', 
              gap: 1.5,
              p: 3
          }}>
              <LocationOnIcon /> Update Assigned Location
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3, bgcolor: '#f8fafc' }}>
             <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>PUNCHING ID</Typography>
                        <TextField
                            label="User Punching ID"
                            fullWidth
                            value={newUserLocation.userpunchingid || ''}
                            onChange={e => setNewUserLocation({...newUserLocation, userpunchingid: e.target.value})}
                            placeholder="e.g. EMP-001"
                            sx={{ bgcolor: 'white', mb: 2 }}
                        />
                    </Box>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>LOCATION DETAILS</Typography>
                        <TextField 
                            label="Address" 
                            fullWidth 
                            value={newUserLocation.address} 
                            onChange={e => setNewUserLocation({...newUserLocation, address: e.target.value})}
                            sx={{ bgcolor: 'white', mb: 2 }}
                        />
                        <Box sx={{ p: 2, bgcolor: '#eff6ff', borderRadius: 2, border: '1px solid #bfdbfe' }}>
                            <Typography variant="caption" color="#2563eb" fontWeight="bold" display="block">SELECTED COORDINATES</Typography>
                            <Typography variant="body2" fontWeight="800" sx={{ mt: 0.5, color: '#1e3a8a' }}>{newUserLocation.lat?.toFixed(6) ?? 0}, {newUserLocation.lng?.toFixed(6) ?? 0}</Typography>
                            <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#3b82f6' }}>
                                Click on the map to drop a pin.
                            </Typography>
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={12} md={8}>
                    <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>MAP PIN</Typography>
                    <Box sx={{ height: 400, borderRadius: 3, overflow: 'hidden', border: '1px solid #e2e8f0', width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <MapContainer center={newUserLocation.lat ? [newUserLocation.lat, newUserLocation.lng] : [25, 45]} zoom={10} style={{ height: '100%', width: '100%' }}>
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                            <LocationMarker 
                                position={newUserLocation.lat ? [newUserLocation.lat, newUserLocation.lng] : null} 
                                setPosition={(pos) => setNewUserLocation(prev => ({ ...prev, lat: pos.lat, lng: pos.lng }))} 
                            />
                        </MapContainer>
                    </Box>
                </Grid>
             </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', gap: 1 }}>
               <Button onClick={() => setEditLocOpen(false)} sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Cancel</Button>
               <Button variant="contained" onClick={handleUpdateLocation} sx={{ borderRadius: 2, px: 3, fontWeight: 'bold' }}>Update</Button>
          </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PunchingDetails;
